import Vue from 'vue';
// eslint-disable-next-line import/no-unresolved
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import {
  HOME_LOCATION_HANDLE,
  NON_SHIPPABLE_PRODUCT_TYPES,
  productTypes,
  SERVICE_PRODUCT_KEYS,
} from '@/constants/constants';
import multiplyTestSpeedPrice from '@/utils/multiplyTestSpeedPrice';

Vue.use(Vuex);

const defaultDiseaseTestData = {
  testType: '',
  extraData: null,
};

/**
 * Products are authored per site in Craft, so their text is translatable while
 * their identity, price and flags are not. The cart is persisted and survives a
 * language switch, so a cart filled on both sites used to render a mix of Dutch
 * and English.
 *
 * The page inlines every product's text in every language
 * (ZizVariable::getProductTranslations), and it is re-applied by id on read, so
 * the cart always renders in the language being read. The text a product was
 * stored with stays on it as a fallback, for a product the page does not list.
 */

// Everything behind an order reads Dutch - the dashboard included - and
// analytics keys a product by its name, so the Dutch title has to be reported
// whatever site the visitor is on. Otherwise one product splits into two items.
const PRIMARY_LOCALE = 'nl';

const localizeProduct = (product, translations) => (
  product && translations[product.id]
    ? { ...product, ...translations[product.id] }
    : product
);

const localizeProducts = (products, translations) => (
  Array.isArray(products)
    ? products.map((product) => localizeProduct(product, translations))
    : products
);

// The products endpoint answers with named lists (required, recommended,
// common, services) alongside plain flags, so only its arrays hold products.
const localizeCatalogue = (catalogue, translations) => Object.keys(catalogue).reduce(
  (acc, key) => {
    acc[key] = localizeProducts(catalogue[key], translations);

    return acc;
  },
  {},
);

/**
 * The text for one locale, keyed by product id, with the Dutch title alongside
 * it as `primaryTitle`. See helpers/gtmProduct.
 */
const translationsForLocale = (productTranslations, locale) => {
  const current = productTranslations[locale] || {};
  const primary = productTranslations[PRIMARY_LOCALE] || {};

  return [...new Set([...Object.keys(current), ...Object.keys(primary)])].reduce(
    (acc, id) => {
      acc[id] = { ...current[id] };

      if (primary[id]?.title) {
        acc[id].primaryTitle = primary[id].title;
      }

      return acc;
    },
    {},
  );
};

/**
 * Cart lines are localized on the way in, not only on the way out.
 *
 * Reading is enough for display, but analytics reports a line's Dutch title
 * from the line itself, and that has to survive a page that has no dictionary -
 * the payment return page reports the purchase without one, and the dictionary
 * is not persisted. Products also reach the cart straight off the products
 * endpoint, with no text of ours on them at all.
 */
const localizeForCart = (state, product) => localizeProduct(
  product,
  translationsForLocale(state.productTranslations, state.siteLocale),
);

// Whether the cart holds anything that has to be posted to the patient. Result
// times (testSpeed) are not goods, and neither are the service fees or the
// e-consult, whose photos are uploaded and answered online.
const hasShippableProduct = (state) => state.cart.some(
  (product) => product
    // Only available products reach the server, so only they may decide the fee.
    && product.available
    && product.entryType === productTypes.DEFAULT_TYPE
    && !NON_SHIPPABLE_PRODUCT_TYPES.includes(product.productType),
);

const syncDeliveryCostsProduct = (state) => {
  const deliveryCostsProduct = state.allProducts.services?.find(
    (product) => product.key === SERVICE_PRODUCT_KEYS.SHIPPING_FEE && product.available,
  );

  state.cart = state.cart.filter(
    (product) => product && product.key !== SERVICE_PRODUCT_KEYS.SHIPPING_FEE,
  );

  // An e-consult-only home order has nothing to deliver, so it keeps the home
  // option without a fee rather than losing it over an unavailable one.
  if (!state.isHomeTest || !hasShippableProduct(state)) {
    return;
  }

  if (deliveryCostsProduct) {
    state.cart.push(localizeForCart(state, deliveryCostsProduct));

    return;
  }

  state.isHomeTest = false;
  state.deliveryEnabled = false;
};

export default new Vuex.Store({
  state: {
    siteLocale: 'nl',
    anamnesis: [],
    cart: [],
    // Ids of cart products that were added for the patient, not by them (required
    // tests, fees, combo and mandatory companions). Only analytics reads it: a removal
    // reports whether the product had been put there automatically (ZIZ-1308).
    autoAddedIds: [],
    // Set when the consult is submitted and consumed by the results page, so
    // consult_complete is reported once per consult, not on every results load.
    consultCompletePending: false,
    cartListToDisplay: null,
    // { [locale]: { [productId]: { title, subTitle, ... } } }, inlined by the
    // page and deliberately not persisted - see the plugins option below.
    productTranslations: {},
    care: true,
    agreement: false,
    loading: false,
    user: {
      firstName: '',
      // The last name split around a Dutch prefix ("van Dam" -> "van" + "Dam"), resolved
      // by the checkout form. `lastName` always keeps the name as the patient typed it.
      lastName: '',
      namePrefix: '',
      lastNameNoPrefix: '',
      birthdayDate: '',
      email: '',
      postcode: '',
      number: '',
      additional: '',
      street: '',
      city: '',
      isAddressManual: false,
      ZIZGaaga: false,
      gender: undefined,
      trans: false,
      delivery: {
        street: '',
        city: '',
        postcode: '',
        number: '',
        additional: '',
        isAddressManual: false,
      },
    },
    donation: null,
    allProducts: {},
    diseaseTestData: { ...defaultDiseaseTestData },
    riskClass: 0,
    resultPageSteps: 0,
    resultsTitle: null,
    deliveryEnabled: false,
    userFormData: null,
    isPrep: false,
    isHomeTest: false,
    nonRecommendedNoticeShown: false,
    paymentMethod: '',
  },
  getters: {
    SITE_LOCALE: (state) => state.siteLocale,
    ANAMNESIS: (state) => state.anamnesis,
    IS_ANAMNESIS_VALID: (state) => Array.isArray(state.anamnesis) && state.anamnesis.length !== 0,
    USER: (state) => state.user,
    CHECKOUT_PATIENT: (state) => {
      const { lastNameNoPrefix, ...patient } = state.user;

      if (lastNameNoPrefix) {
        return { ...patient, lastName: lastNameNoPrefix };
      }

      // A checkout that was already past the address step when this shipped has no
      // lastNameNoPrefix stored, only the resolved prefix; take it off the name as typed
      // rather than send the prefix twice.
      const prefix = patient.namePrefix;
      const lastName = prefix && patient.lastName.startsWith(`${prefix} `)
        ? patient.lastName.slice(prefix.length).trim()
        : patient.lastName;

      return { ...patient, lastName };
    },
    USER_ADDRESS: (state) => ({
      street: state.user.street,
      city: state.user.city,
      postcode: state.user.postcode,
      number: state.user.number,
      additional: state.user.additional,
      isAddressManual: state.user.isAddressManual,
    }),
    USER_DELIVERY_ADDRESS: (state) => ({
      street: state.user.delivery?.street || '',
      city: state.user.delivery?.city || '',
      postcode: state.user.delivery?.postcode || '',
      number: state.user.delivery?.number || '',
      additional: state.user.delivery?.additional || '',
      isAddressManual: state.user.delivery?.isAddressManual || false,
    }),
    CARE: (state) => state.care,
    /**
     * The text for the language being read, keyed by product id, with the Dutch
     * title alongside it as `primaryTitle` - that is what analytics reports, so
     * every product the store hands out carries it and no call site has to look
     * it up. See helpers/gtmProduct.
     */
    PRODUCT_TRANSLATIONS: (state) => translationsForLocale(
      state.productTranslations,
      state.siteLocale,
    ),
    CART_LIST_TO_DISPLAY: (state, getters) => localizeProducts(
      state.cartListToDisplay,
      getters.PRODUCT_TRANSLATIONS,
    ),
    CART_NO_SORT: (state, getters) => localizeProducts(
      state.cart.filter((product) => product && product.available),
      getters.PRODUCT_TRANSLATIONS,
    ),
    CART: (state, getters) => {
      const cart = getters.CART_NO_SORT;

      return multiplyTestSpeedPrice(cart).sort((a, b) => {
        if (a.letter === null || a.letter === undefined) return 1;
        if (b.letter === null || b.letter === undefined) return -1;
        const letterA = a.letter.toLowerCase();
        const letterB = b.letter.toLowerCase();
        if (letterA === 't') return -1; // T always on top
        if (letterB === 't') return 1;
        return letterA.localeCompare(letterB);
      });
    },
    CART_DEFAULT_TYPE: (state, getters) => getters.CART_NO_SORT.filter(
      (product) => product.entryType === productTypes.DEFAULT_TYPE,
    ),
    CART_DIAGNOSTIC_PRODUCTS: (state, getters) => getters.CART_DEFAULT_TYPE.filter(
      (product) => !['service'].includes(product.productType),
    ),
    CART_TEST_SPEED_TYPE: (state, getters) => getters.CART_NO_SORT.filter(
      (product) => product.entryType === productTypes.TEST_SPEED_TYPE,
    ),
    // The cart holds a real test or medication rather than only fees and the
    // e-consult. Gates the shipping fee and every claim that rests on it: the
    // delivery copy on the test-method cards and the treatment note beneath them.
    HAS_SHIPPABLE_PRODUCTS: (state) => hasShippableProduct(state),
    CART_SUM: (state, getters) => (getters.CART
      // eslint-disable-next-line default-param-last
      .reduce((sum = 0, elem) => sum + elem.cost, 0) + (state.donation ?? 0)
    ),
    CART_DONATION: (state) => state.donation,
    IS_BLOOD_TEST_EXISTS: (state) => state.cart.find((product) => product && product.type === 'bloed'),
    ALL_PRODUCTS: (state, getters) => localizeCatalogue(
      state.allProducts,
      getters.PRODUCT_TRANSLATIONS,
    ),
    CONSULT_SERVICE_FEE_PRODUCT: (state) => state.allProducts.services?.find(
      (product) => product.key === 'CONSULT_SERVICE_FEE' && product.available,
    ),
    DELIVERY_COSTS_PRODUCT: (state) => state.allProducts.services?.find(
      (product) => product.key === SERVICE_PRODUCT_KEYS.SHIPPING_FEE && product.available,
    ),
    DELIVERY_ENABLED: (state) => state.deliveryEnabled,
    IS_HOME_TEST: (state) => state.isHomeTest,
    // The location the visit resolves to. A test method without branch locations
    // (home collection) leaves the extra data empty and resolves to home.
    CHECKOUT_LOCATION_HANDLE: (state) => state.diseaseTestData.extraData?.location?.handle
      || HOME_LOCATION_HANDLE,
    IS_HOME_LOCATION: (state, getters) => getters.CHECKOUT_LOCATION_HANDLE === HOME_LOCATION_HANDLE,
    NON_RECOMMENDED_NOTICE_SHOWN: (state) => state.nonRecommendedNoticeShown,
    DISEASE_TEST_TYPE: (state) => state.diseaseTestData.testType,
    DISEASE_TEST_EXTRA_DATA: (state, getters) => {
      const { extraData } = state.diseaseTestData;

      if (!Array.isArray(extraData?.locationTestProducts)) {
        return extraData;
      }

      return {
        ...extraData,
        locationTestProducts: localizeProducts(
          extraData.locationTestProducts,
          getters.PRODUCT_TRANSLATIONS,
        ),
      };
    },
    RISK_CLASS: (state) => state.riskClass,
    RESULT_PAGE_STEPS: (state) => state.resultPageSteps,
    RESULTS_TITLE: (state) => state.resultsTitle,
    AGREEMENT: (state) => state.agreement,
    USER_FORM_DATA: (state) => state.userFormData,
    IS_PREP: (state, getters) => state.isPrep && getters.CART.filter(
      (p) => p.strictlyDisablePrep,
    ).length === 0,
    PAYMENT_METHOD: (state) => state.paymentMethod,
  },
  mutations: {
    SET_SITE_LOCALE: (state, payload) => {
      state.siteLocale = payload;
    },
    SET_PRODUCT_TRANSLATIONS: (state, payload) => {
      state.productTranslations = payload || {};
    },
    SET_CART_LIST_TO_DISPLAY: (state, payload) => {
      state.cartListToDisplay = payload;
    },
    SET_USER_FORM_DATA: (state, payload) => {
      state.userFormData = payload;
    },
    SET_DELIVERY_ENABLED: (state, payload) => {
      state.deliveryEnabled = payload;
    },
    SET_NON_RECOMMENDED_NOTICE_SHOWN: (state, payload) => {
      state.nonRecommendedNoticeShown = payload;
    },
    SET_IS_HOME_TEST: (state, payload) => {
      state.isHomeTest = payload;
      syncDeliveryCostsProduct(state);
    },
    SET_USER: (state, payload) => {
      Vue.set(state, 'user', payload);
    },
    SET_ANAMNESIS: (state, payload) => {
      state.anamnesis = payload;
    },
    SET_CARE: (state, care) => {
      state.care = care;
    },
    SET_AGREEMENT: (state, agreement) => {
      state.agreement = agreement;
    },
    SET_ALL_PRODUCTS: (state, products) => {
      state.allProducts = products;
      syncDeliveryCostsProduct(state);
    },
    SET_DISEASE_TEST_TYPE: (state, testType) => {
      state.diseaseTestData.testType = testType;
    },

    SET_DISEASE_TEST_EXTRA_DATA: (state, data) => {
      state.diseaseTestData.extraData = data;
    },

    SET_RISK_CLASS: (state, riskClass) => {
      state.riskClass = riskClass;
    },

    SET_RESULT_PAGE_STEPS: (state, stepsNumber) => {
      state.resultPageSteps = stepsNumber;
    },

    SET_RESULTS_TITLE: (state, resultsTitle) => {
      state.resultsTitle = resultsTitle;
    },

    ADD_TO_CART: (state, payload) => {
      // Older builds indexed the cart by product id, producing a sparse array
      // padded with null slots. Drop any such legacy entries on write.
      state.cart = state.cart.filter(Boolean);

      // Products reach here straight off the products endpoint as well as from
      // the store, so localize on the way in - see localizeForCart.
      const lineItem = localizeForCart(state, payload);

      // Add the product, replacing any existing entry for the same id.
      const index = state.cart.findIndex((product) => product.id === payload.id);
      if (index === -1) {
        state.cart.push(lineItem);
      } else {
        Vue.set(state.cart, index, lineItem);
      }

      // Mutually exclusive diagnoses.
      // Each diagnosis has a letter.
      // The key is a diagnosis that combines the diagnoses from the array.
      const listProducts = {
        T: ['A', 'B', 'C', 'D'],
        Y: ['B', 'D'],
        Z: ['A', 'D'],
        N: ['B', 'C'],
        J: ['B', 'C', 'D'],
      };
      // PrEP products are not allowed to be combined with each-other
      // If an additional PrEP product is selected,
      // the other PrEP product should be removed from basket.
      if (['W', 'X', 'V'].includes(payload.letter)) {
        state.cart = state.cart.filter(
          (item) => !(['W', 'X', 'V'].includes(item.letter) && item.letter !== payload.letter),
        );
      }

      const lettersToRemove = new Set();
      Object.entries(listProducts).forEach(([combined, parts]) => {
        if (payload.letter === combined) {
          parts.forEach((letter) => lettersToRemove.add(letter));
        }
        if (parts.includes(payload.letter)) {
          lettersToRemove.add(combined);
        }
      });
      if (lettersToRemove.size > 0) {
        state.cart = state.cart.filter((item) => !lettersToRemove.has(item.letter));
      }

      syncDeliveryCostsProduct(state);

      // Add mandatory product 'O' if 'V' is added
      if (payload.letter === 'V') {
        const mandatoryProducts = ['O'];
        mandatoryProducts.forEach((letter) => {
          const product = Array.isArray(state.allProducts)
            ? state.allProducts.find((item) => item.letter === letter)
            : undefined;
          if (product && !state.cart.some((item) => item.letter === letter)) {
            state.cart.push(product);
            if (!state.autoAddedIds.includes(product.id)) state.autoAddedIds.push(product.id);
          }
        });
      }
    },

    REMOVE_FROM_CART: (state, payload) => {
      // If click the Kreatinine (U) or HIV-test (L) out of the basket,
      // the PrEP (W, X, V) should leave the basket too.
      const prepLettersToRemove = ['U', 'L'].includes(payload.letter)
        ? ['W', 'X', 'V']
        : [];

      // Remove the product (and any dependent PrEP products), and compact away
      // any legacy null entries from older builds in one pass.
      state.cart = state.cart.filter(
        (product) => product
          && product.id !== payload.id
          && !prepLettersToRemove.includes(product.letter),
      );

      syncDeliveryCostsProduct(state);
    },

    // Cart changes push no analytics themselves: most of them are not the patient's
    // choice. The mixins' addToCart/removeFromCart report the deliberate ones (ZIZ-1308).
    SET_CONSULT_COMPLETE_PENDING: (state, pending) => {
      state.consultCompletePending = pending;
    },

    MARK_AUTO_ADDED: (state, id) => {
      if (!state.autoAddedIds.includes(id)) state.autoAddedIds.push(id);
    },

    UNMARK_AUTO_ADDED: (state, id) => {
      state.autoAddedIds = state.autoAddedIds.filter((autoId) => autoId !== id);
    },

    SET_DONATION: (state, payload) => {
      state.donation = payload;
    },

    SET_IS_PREP: (state, payload) => {
      state.isPrep = payload;
    },

    CLEAR_DONATION: (state) => {
      state.donation = null;
    },

    CLEAR_DATA: (state) => {
      state.cart = [];
      state.autoAddedIds = [];
      state.cartListToDisplay = null;
      state.anamnesis = [];
      state.care = true;
      state.agreement = false;
      state.loading = false;
      state.user = {};
      state.donation = null;
      state.diseaseTestData = { ...defaultDiseaseTestData };
      state.riskClass = 0;
      state.resultPageSteps = 0;
      state.deliveryEnabled = false;
      state.isHomeTest = false;
      state.nonRecommendedNoticeShown = false;
      state.userFormData = null;
    },
    CLEAR_CART: (state) => {
      state.cart = [];
      state.autoAddedIds = [];
    },

    CLEAR_DISEASE_EXTRA_DATA: (state) => {
      state.diseaseTestData = {
        ...state.diseaseTestData,
        extraData: defaultDiseaseTestData.extraData,
      };
    },

    CLEAR_DISEASE_TEST_DATA: (state) => {
      state.diseaseTestData = { ...defaultDiseaseTestData };
    },

    SET_PAYMENT_METHOD: (state, payload) => { state.paymentMethod = payload; },
  },
  plugins: [
    createPersistedState({
      // Persist everything except the text: the page inlines it fresh on every
      // load, so storing a copy would only take up room and risk rendering
      // yesterday's text after a content edit. Excluded from writes only - on
      // read the store's own default stands, since there is nothing to merge.
      reducer: ({ productTranslations, ...state }) => state,
    }),
  ],
});
